import React from 'react'
import { inject, observer } from 'mobx-react'
import { Checkbox, Input, Radio, Skeleton, Switch, message } from 'antd-v4'
import { ReceiptApi, ReceiptModels, ReceiptUtils } from '@getgreenline/receipts'
import { ExternalLoyaltyModels } from '@getgreenline/external-loyalty'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { sampleLoyaltyData, samplePaymentForReceipt } from './receiptHelpers'
import { parseErrorMsg } from '../../../utilities/helpers'
import { LocationModels } from '@getgreenline/locations'
import { isNil } from 'lodash'
import { IPayment } from '@getgreenline/homi-shared'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { ReceiptV3 } from '../../../components/Receipt/ReceiptV3'
import { ReceiptV2 } from '../../../components/Receipt/ReceiptV2'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  selectedLocation: LocationModels.ILocationContract
}

interface State extends ReceiptModels.IReceiptContract {
  isLoading: boolean
  headerOption: ReceiptModels.RECEIPT_HEADER_OPTIONS
  showLoyaltyInfoToggle: boolean
}

@inject('currentCompanyStore')
@observer
export class ReceiptFormV2 extends React.Component<Props & InjectedProps, State> {
  company = this.props.currentCompanyStore!.company!

  defaultReceiptSettings = ReceiptUtils.generateDefaultReceiptSettings(
    this.company,
    this.props.selectedLocation.id,
  )

  constructor(props: Props & InjectedProps) {
    super(props)

    this.state = {
      ...this.defaultReceiptSettings,
      isLoading: false,
      locationId: this.props.selectedLocation.id,
      showLoyaltyInfoToggle: false,
    }
  }

  componentDidMount = async () => {
    const companyStore = this.props.currentCompanyStore!

    this.getReceiptSettings(this.props.selectedLocation)

    await this.props.currentCompanyStore?.getExternalLoyaltyProvider(companyStore!.company!.id)
    await this.props.currentCompanyStore?.reloadCompany()
    this.setState({
      showLoyaltyInfoToggle: this.props.currentCompanyStore!.company!.permissions.canAccessLoyalty,
    })

    companyStore.getCanUseLoyaltyService()
    if (!companyStore.externalLoyaltyProvider)
      companyStore.getExternalLoyaltyProvider(this.company.id)
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.selectedLocation.id !== this.props.selectedLocation.id) {
      this.getReceiptSettings(nextProps.selectedLocation)
    }
  }

  getMainHeaderValue = (overrideOptions?: {
    headerOption?: ReceiptModels.RECEIPT_HEADER_OPTIONS
    header?: string | null
  }): string => {
    const option = overrideOptions?.headerOption || this.state.headerOption

    switch (option) {
      case ReceiptModels.RECEIPT_HEADER_OPTIONS.USE_COMPANY_NAME:
        return this.company.name

      case ReceiptModels.RECEIPT_HEADER_OPTIONS.USE_LOCATION_NAME:
        return this.props.selectedLocation.name || ''

      case ReceiptModels.RECEIPT_HEADER_OPTIONS.USE_CUSTOM:
        return overrideOptions?.header || ''

      default:
        return this.state.header || ''
    }
  }

  getAddressLine = (headerOption: ReceiptModels.RECEIPT_HEADER_OPTIONS): string | null => {
    if (headerOption === ReceiptModels.RECEIPT_HEADER_OPTIONS.USE_LOCATION_NAME) {
      const { address, city, province, postalCode } = this.props.selectedLocation

      const addressLines = [address, city, province, postalCode]
        .map((str) => str?.trim())
        .filter((str) => !isNil(str) && str.length > 0)

      return addressLines.join(', ')
    }

    return this.state.address
  }

  populateReceiptSettings = (settings?: ReceiptModels.IReceiptContract) => {
    if (settings) {
      const header = this.getMainHeaderValue({
        headerOption: settings?.headerOption,
        header: settings?.header,
      })

      this.setState({
        ...settings,
        header,
      })

      // Update receipt settings in store
      this.props.currentCompanyStore?.customReceiptSettings.set(settings.locationId, settings)
    } else {
      this.setState({
        ...this.defaultReceiptSettings,
        id: undefined,
      })
    }
  }

  getReceiptSettings = async (selectedLocation: LocationModels.ILocationContract) => {
    try {
      this.setState({ isLoading: true })

      const settings = await ReceiptApi.getReceipt(this.company.id, selectedLocation.id)

      this.populateReceiptSettings(settings)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading receipt settings')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  save = async () => {
    const locationId = this.props.selectedLocation.id

    try {
      this.setState({ isLoading: true })

      // Set null if not using custom header
      const header =
        this.state.headerOption === ReceiptModels.RECEIPT_HEADER_OPTIONS.USE_CUSTOM
          ? this.state.header
          : null

      const settings = {
        ...this.state,
        locationId,
        header,
      }

      const newSettings = this.state.id
        ? await ReceiptApi.updateReceipt(this.company.id, settings)
        : await ReceiptApi.createReceipt(this.company.id, settings)

      this.props.currentCompanyStore?.customReceiptSettings.set(locationId, newSettings)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while saving receipt settings')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  get disableMainHeader(): boolean {
    return this.state.headerOption !== ReceiptModels.RECEIPT_HEADER_OPTIONS.USE_CUSTOM
  }

  get samplePayment(): IPayment {
    return {
      ...samplePaymentForReceipt,
      locationName: this.props.selectedLocation.name || '',
    }
  }

  onToggleLoyaltyQr = (isChecked: boolean) => {
    const externalLoyaltyConfig = this.props.currentCompanyStore!.externalLoyaltyProviderConfig
    this.setState({
      showLoyaltyWallet: isChecked,
      loyaltyWalletUrl:
        externalLoyaltyConfig && isChecked
          ? `https://lab.alpineiq.com/wallet/${externalLoyaltyConfig.externalMerchantId}`
          : null,
    })
  }

  render() {
    const wrapperClassName = 'd-flex flex-wrap align-items-center mb-4'
    const labelClassName = 'pl-0 mb-1 font-weight-bold text-muted'
    const isAlpineIQ =
      this.props.currentCompanyStore!.externalLoyaltyProvider ===
      ExternalLoyaltyModels.ExternalLoyaltyProviders.ALPINE_IQ

    const shouldShowLoyaltyPreview = this.state.showLoyalty && this.state.showLoyaltyInfoToggle

    if (this.state.isLoading) {
      return <Skeleton active />
    }

    return (
      <div className='row mt-4'>
        <section className='col-sm-4 mb-4'>
          <div className='d-flex flex-column mb-4'>
            <label className='font-weight-bold text-muted'>Header</label>

            <Radio.Group
              value={this.state.headerOption}
              onChange={(e) => {
                const headerOption = e.target.value as ReceiptModels.RECEIPT_HEADER_OPTIONS
                const header = this.getMainHeaderValue({ headerOption })
                const address = this.getAddressLine(headerOption)

                this.setState({ headerOption, header, address })
              }}
            >
              {Object.entries(ReceiptModels.RECEIPT_HEADER_OPTIONS).map(([key, value]) => (
                <Radio key={key} value={value}>
                  {ReceiptUtils.getHeaderOptionName(value)}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          <div className={wrapperClassName}>
            <label className={labelClassName}>Custom header</label>
            <Input.TextArea
              rows={1}
              disabled={this.disableMainHeader}
              placeholder='Please enter header'
              value={this.state.header || ''}
              onChange={(e) => this.setState({ header: e.target.value })}
            />
          </div>

          <div className={wrapperClassName}>
            <label className={labelClassName}>Sub-header</label>
            <Input.TextArea
              rows={1}
              placeholder='Please enter sub-header'
              value={this.state.subHeader || ''}
              onChange={(e) => this.setState({ subHeader: e.target.value })}
            />
          </div>

          <div className={wrapperClassName}>
            <label className={labelClassName} style={{ wordBreak: 'break-all' }}>
              Description
            </label>
            <Input.TextArea
              rows={1}
              placeholder='Please enter description'
              value={this.state.description || ''}
              onChange={(e) => this.setState({ description: e.target.value })}
            />
          </div>

          <div className={wrapperClassName}>
            <label className={labelClassName}>Address</label>
            <Input.TextArea
              rows={1}
              placeholder='Please enter address'
              value={this.state.address || ''}
              onChange={(e) => this.setState({ address: e.target.value })}
            />
          </div>

          <div className={wrapperClassName}>
            <label className={labelClassName}>Footer text</label>
            <Input.TextArea
              rows={3}
              placeholder='Please enter footer text'
              value={this.state.footer || ''}
              onChange={(e) => this.setState({ footer: e.target.value })}
            />
          </div>

          <div className='form-group'>
            <Checkbox
              checked={this.state.showEmployee}
              onChange={() => this.setState({ showEmployee: !this.state.showEmployee })}
            >
              Show employee name
            </Checkbox>
          </div>

          <div className='form-group'>
            <Checkbox
              checked={this.state.showSku}
              onChange={() => this.setState({ showSku: !this.state.showSku })}
            >
              Show SKUs
            </Checkbox>
          </div>

          {this.state.showLoyaltyInfoToggle && (
            <div className='d-xl-flex align-items-xl-center justify-content-xl-between mb-4 text-muted'>
              <div>
                <b>Show Loyalty info on Receipt</b>

                <div className='mt-1'>
                  If toggle is turned on, customers will be able to see loyalty information such as
                  balance on the receipt.
                </div>
              </div>

              <div className='ml-xl-2 mt-sm-1 mt-xl-0'>
                <Switch
                  checked={this.state.showLoyalty}
                  onChange={(enabled) => this.setState({ showLoyalty: enabled })}
                />
              </div>
            </div>
          )}

          {this.props.currentCompanyStore!.canUseLoyaltyService && isAlpineIQ && (
            <div className='d-xl-flex align-items-xl-center justify-content-xl-between mb-4 text-muted'>
              <div>
                <b>Show Loyalty Wallet QR Code on Receipt</b>

                <div className='mt-1'>
                  If toggle is turned on, customers will be able to see their Loyalty Wallet URL in
                  a QR Code on the receipt.
                </div>
              </div>

              <div className='ml-xl-2 mt-sm-1 mt-xl-0'>
                <Switch
                  checked={this.state.showLoyaltyWallet}
                  onChange={(isChecked) => this.onToggleLoyaltyQr(isChecked)}
                />
              </div>
            </div>
          )}

          <BlazeButton
            titleProps={{
              title: 'Save',
            }}
            buttonProps={{
              type: 'primary',
              onClick: this.save,
            }}
          />
        </section>

        <section className='col-sm-6'>
          <div className='ml-4 mr-4 p-2 background-shadow' style={{ maxWidth: '20rem' }}>
            {this.props.currentCompanyStore!.canUseLoyaltyService ? (
              <ReceiptV3
                company={this.company}
                payment={this.samplePayment}
                receiptSettings={this.state}
                loyaltyData={shouldShowLoyaltyPreview ? sampleLoyaltyData : undefined}
                loyaltyWalletUrl={
                  isAlpineIQ && this.state.showLoyaltyWallet
                    ? 'https://getgreenline.co/'
                    : undefined
                }
              />
            ) : (
              <ReceiptV2
                company={this.company}
                payment={this.samplePayment}
                receiptSettings={this.state}
                loyaltyData={shouldShowLoyaltyPreview ? sampleLoyaltyData : undefined}
              />
            )}
          </div>
        </section>
      </div>
    )
  }
}
